import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, CheckCircle2, XCircle, Clock, Filter, Mail, Calendar, MessageSquare
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Pending', icon: Clock },
  approved: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Approved', icon: CheckCircle2 },
  rejected: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Rejected', icon: XCircle },
};

const CommitteeApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const res = await axios.get(`${API}/admin/committees/applications`, { withCredentials: true });
      setApplications(res.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (id, status) => {
    setProcessing(prev => ({ ...prev, [id]: true }));
    try {
      await axios.put(`${API}/admin/committees/applications/${id}?status=${status}`, {}, { withCredentials: true });
      toast.success(`Application ${status}!`);
      fetchApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setProcessing(prev => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-committee-applications">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#002249]">Committee Applications</h1>
          <p className="text-slate-600 text-sm mt-1">Review and approve committee membership requests.</p>
        </div>
        {applications.length > 0 && (
          <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-sm text-sm font-semibold">{applications.length} pending</span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
      ) : applications.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-medium">No pending applications</p>
          <p className="text-sm mt-1">All committee applications have been reviewed.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map((app, index) => (
            <motion.div
              key={app.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 * index }}
              className="bg-white border border-slate-200 rounded-sm overflow-hidden"
              data-testid={`committee-app-${index}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-purple-100 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#002249] mb-0.5">{app.committee_name}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600 mb-2">
                        <span className="flex items-center"><Users className="w-3.5 h-3.5 mr-1 text-slate-400" />{app.user_name}</span>
                        <span className="flex items-center"><Calendar className="w-3.5 h-3.5 mr-1 text-slate-400" />{new Date(app.created_at).toLocaleDateString()}</span>
                      </div>
                      {app.message && (
                        <div className="bg-slate-50 border border-slate-100 rounded-sm p-3 mt-2">
                          <p className="text-xs font-medium text-slate-500 mb-1 flex items-center"><MessageSquare className="w-3 h-3 mr-1" />Application Message</p>
                          <p className="text-sm text-slate-700">{app.message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 flex-shrink-0">
                    <Button
                      onClick={() => handleDecision(app.id, 'approved')}
                      disabled={processing[app.id]}
                      className="bg-green-600 hover:bg-green-700 rounded-sm"
                      data-testid={`approve-app-${index}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />Approve
                    </Button>
                    <Button
                      onClick={() => handleDecision(app.id, 'rejected')}
                      disabled={processing[app.id]}
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm"
                      data-testid={`reject-app-${index}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />Reject
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommitteeApplicationsPage;
